/**
 * The small things that should survive a reload.
 *
 * ## Why a helper rather than `persist()`
 *
 * Zustand ships middleware for this and it is not used here, because the two
 * stores that want it want *part* of themselves persisted and the rest
 * deliberately not - the studio's settings but not whether it is open, the
 * console's column widths but nothing else. Expressing that through `partialize`
 * and a `merge` is more configuration than the fifteen lines below, and it hides
 * where the write happens.
 *
 * `stores/player.ts` predates this and keeps its own copy. It is not converted
 * because its write is throttled around a `position` that updates four times a
 * second and trims its queue on the way out - genuinely different needs, and
 * rewriting working playback persistence to share a helper would be a change
 * with no upside.
 *
 * ## Everything here fails quietly
 *
 * Private browsing, storage disabled, a quota that is full, a value written by
 * an older build with a different shape. None of those should stop the studio
 * from opening or the console from rendering, so every path returns something
 * usable rather than throwing.
 */

/**
 * What was stored under this key, or nothing.
 *
 * Deliberately typed as `Partial<T>`: what comes back was written by whatever
 * version of the app the user last ran, and treating it as a complete `T` is how
 * a renamed field becomes `undefined` at a call site that swore it could not be.
 * Callers validate what they care about.
 */
export function readStored<T>(key: string): Partial<T> {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);

    // An array or a bare number would satisfy `JSON.parse` and then spread into
    // nonsense. Only an object is a shape this can be read as.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    return parsed as Partial<T>;
  } catch {
    return {};
  }
}

/** Pending writes, so a burst of changes costs one write rather than fifty. */
const pending = new Map<string, { timer: number; value: unknown }>();

/**
 * Store a value, coalescing rapid changes.
 *
 * ## Trailing, not leading
 *
 * A leading throttle - write now, ignore anything for the next N milliseconds -
 * is what the player uses, and it is wrong here. Dragging a level slider ends
 * with the value the host let go on, and a leading throttle drops precisely that
 * one: the first move of the drag is written and the last is discarded. The
 * setting would then come back at whatever it passed through on the way.
 *
 * So the timer restarts on every change and only the final value lands.
 */
export function writeStored(key: string, value: unknown, delay = 250): void {
  const existing = pending.get(key);

  if (existing) window.clearTimeout(existing.timer);

  pending.set(key, {
    value,
    timer: window.setTimeout(() => flush(key), delay),
  });
}

/** Write one pending value now. */
function flush(key: string): void {
  const entry = pending.get(key);

  if (!entry) return;

  window.clearTimeout(entry.timer);
  pending.delete(key);

  try {
    localStorage.setItem(key, JSON.stringify(entry.value));
  } catch {
    // Storage full or unavailable. Losing a preference is not worth breaking
    // the page that was setting it.
  }
}

/**
 * Write everything still waiting, immediately.
 *
 * ## Why this is not optional
 *
 * Coalescing means there is always a window - a quarter of a second - in which
 * a change exists only as a pending timer. Leaving the page inside that window
 * destroys the timer and the change with it, and the way somebody hits that
 * window is by doing the obvious thing: drag a divider to where they want it,
 * then reload to check it stuck. It would not have.
 *
 * Found exactly that way, by a test that dragged and reloaded.
 */
export function flushStored(): void {
  for (const key of [...pending.keys()]) flush(key);
}

/*
 * Both events, because neither alone is enough.
 *
 * `pagehide` covers reloads, navigations and the tab being closed. It is not
 * reliably delivered when a mobile browser is backgrounded and later killed,
 * which is what `visibilitychange` catches - and that is not an exotic case for
 * a host who switches apps mid-set.
 *
 * `flush` clears what it writes, so being called twice costs nothing.
 */
window.addEventListener("pagehide", flushStored);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flushStored();
});
